import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { decideNaConta } from '../helper/escopos';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  // Rota que existe fora de uma conta: o consentimento do OAuth é do usuário, e
  // o cliente MCP que abre este endereço não tem como saber um id de conta. Sem
  // esta saída, a validação abaixo procura a conta por um `accountId` ausente,
  // não acha e manda o usuário para o login no meio da autorização.
  if (to.meta?.accountAgnostic) {
    return next();
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  // Concluir o cadastro da conta é do administrador (`AccountPolicy#update?`). ACESSO-01 (L8b): com a chave
  // ACESSO_GRANULAR ligada, de quem tem o `roles:manage`, que é o que o servidor pede ali.
  const concluiCadastro = decideNaConta(
    user,
    routeAccountId,
    'roles:manage',
    () => userAccount?.role === 'administrator'
  );
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    concluiCadastro &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    // Pos-login o usuario aterrissa na Home do OS (launcher dos PowerApps), nao direto
    // nas conversas. Quem quer a caixa de conversas chega por PowerChat > Todas as
    // conversas, ou por link direto para /dashboard, que continua valendo.
    const target = needsOnboarding ? 'onboarding' : 'home';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/onboarding`));
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/home`));
  }

  // Super-admin-only sections (PowerAgents, Campaigns, account/automation/integrations/bots
  // settings): redirect a non-super-admin who deep-links there back to the dashboard. isSuperAdmin
  // is platform-level (user.type), orthogonal to the per-account admin role checked above.
  const isSuperAdmin = user?.type === 'SuperAdmin';
  if (to.matched.some(route => route.meta?.superAdminOnly) && !isSuperAdmin) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  // PA-01 (D17): seções de duas faixas — super-admin entra sempre; administrador da conta
  // entra quando a feature flag estiver ligada NAQUELA conta. A meta é separada de
  // `superAdminOnly` de propósito: mudar a semântica daquela afetaria todas as outras
  // seções que a usam. O backend continua sendo a fronteira; isto é só a porta da frente.
  const flagDeDuasFaixas = to.matched
    .map(route => route.meta?.superAdminOrFlag)
    .find(Boolean);
  if (flagDeDuasFaixas && !isSuperAdmin) {
    // Num carregamento direto -- link, favorito, F5 -- este guard roda antes de o App buscar a
    // conta, e sem os dados a flag lia `false`: o cliente era mandado para o painel com a flag
    // ligada (medido no preview em 24/09). O super admin nunca viu, porque não passa por aqui.
    if (!store.getters['accounts/getAccount']?.(routeAccountId)?.features) {
      await store.dispatch('accounts/get', { silent: true });
    }
    const habilitada =
      store.getters['accounts/isFeatureEnabledonAccount']?.(
        routeAccountId,
        flagDeDuasFaixas
      ) ?? false;
    if (!habilitada) {
      return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
    }
  }

  // Deploy do PowerFlow (D2, 16/10): rota que o administrador só alcança com uma chave da instalação
  // ligada (`window.globalConfig`, pelo store `globalConfig`). Sem ela, volta para `semChave.volta`, que
  // diz o motivo. O super-admin não depende da chave, e o backend continua sendo a fronteira.
  const semChave = to.matched.map(route => route.meta?.semChave).find(Boolean);
  const chaveLigada =
    semChave && store.getters['globalConfig/get']?.[semChave.chave] === true;
  if (semChave && !isSuperAdmin && !chaveLigada) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${semChave.volta}`,
        semChave.query
      )
    );
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
